import { s3ProgressConfig } from "../utils/uploadProgress";
import i18n from "../i18n";
import axios from "axios";
import { api } from "./axiosClient";

/** Reads the server's `{ message }` out of a failed blob-typed request. */
async function readErrorMessage(err: any): Promise<string> {
  const data = err?.response?.data;

  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      if (parsed?.message) return parsed.message;
    } catch {
      // Not JSON (a proxy error page, say) — fall through to the generic text.
    }
  } else if (data?.message) {
    return data.message;
  }

  return err?.message ?? i18n.t("downloadFailed", { ns: "common" });
}

/** Pulls the filename out of a Content-Disposition header, preferring the
 *  RFC 5987 `filename*` form so non-ASCII contract numbers survive. */
function parseFileName(disposition?: string): string | null {
  if (!disposition) return null;

  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (encoded?.[1]) {
    try {
      return decodeURIComponent(encoded[1]);
    } catch {
      // Malformed escape — fall through to the plain form below.
    }
  }

  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  return plain?.[1] ?? null;
}

/** Hands a downloaded blob to the browser as a file save. */
export function saveBlobAsFile(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoked on the next tick rather than immediately: Safari cancels an
  // in-flight download if the object URL disappears in the same frame.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const contractApi = {
  searchContracts: async (payload: any) => {
    const response = await api.post("/contract/search", payload);
    return response.data;
  },

  createContract: async (data: any) => {
    const response = await api.post("/contract/create", data);
    return response.data;
  },

  /**
   * Legacy contracts have their own create/edit endpoints. They enter already
   * Signed and carry no workflow, so the backend validates and handles them
   * on a separate path rather than as an exception to the normal one.
   */
  createLegacyContract: async (data: any) => {
    const response = await api.post("/contract/createlegacy", data);
    return response.data;
  },

  editLegacyContract: async (data: any) => {
    const response = await api.post("/contract/editlegacy", data);
    return response.data;
  },

  updateContract: async (data: any) => {
    const response = await api.post("/contract/edit", data);
    return response.data;
  },

  removeContract: async (id: string) => {
    return await api.post("/contract/remove", { id });
  },

  changeStatusContract: async (data: {
    contractId: string;
    status: number;
  }) => {
    return await api.post("/contract/changestatus", data);
  },

  deactivateContract: async (id: string) => {
    return await api.post("/contract/deactivate", { id });
  },

  sendRenewalReminders: async (): Promise<{ remindedCount: number }> => {
    const response = await api.post("/contract/sendrenewalreminders");
    return response.data;
  },

  initUpload: async (ids: string[], groupId?: string) => {
    const response = await api.post("/contract/initupload", { ids, groupId });
    return response.data;
  },

  uploadToS3: async (s3Url: string, file: File) => {
    return await axios.put(s3Url, file, {
      ...s3ProgressConfig(file),
      headers: {
        "Content-Type": file.type,
      },
    });
  },

  uploadMedia: async (contractId: string, medias: any[]) => {
    return await api.post("/contract/uploadmedia", {
      contractId,
      medias,
    });
  },

  /**
   * Downloads a contract document, as a rendered PDF or the stored .docx.
   *
   * Both go through the API rather than a pre-signed S3 link on purpose: a
   * link opened by the browser is a download the server never learns about,
   * and every copy taken of an agreement has to reach the audit log. Pass a
   * `mediaId` for a specific attachment, or omit it for the contract's
   * current document.
   */
  downloadContractDocument: async (
    contractId: string,
    format: "pdf" | "docx" = "pdf",
    mediaId?: string,
  ) => {
    let response;
    try {
      response = await api.post(
        format === "pdf" ? "/contract/downloadpdf" : "/contract/downloaddocx",
        { contractId, mediaId: mediaId ?? null },
        { responseType: "blob" },
      );
    } catch (err: any) {
      // responseType "blob" applies to error bodies too, so the server's
      // {"message": ...} arrives as a Blob and would otherwise surface as
      // "[object Blob]". Unwrap it so the caller can show what went wrong.
      throw new Error(await readErrorMessage(err));
    }

    return {
      blob: response.data as Blob,
      // Server-chosen name — the contract number for a PDF, the stored name
      // for a .docx. Falls back only if the header didn't survive the proxy.
      fileName:
        parseFileName(response.headers?.["content-disposition"]) ??
        `${contractId}.${format}`,
    };
  },

  // Cleanup-only — removes the orphaned version-history row left behind
  // when a workflow-send snapshot upload succeeds but the send itself
  // then fails. Never call this for a user-initiated document delete —
  // that should only go through uploadMedia's Delete request type, which
  // deliberately leaves version history intact.
  removeDocumentVersion: async (contractId: string, mediaId: string) => {
    return await api.post("/contract/removedocumentversion", {
      contractId,
      mediaId,
    });
  },

  getMediaLink: async (
    ids: string[],
    size: number = 3,
    groupId?: string,
  ): Promise<{ links: Array<{ id: string; link: string }> }> => {
    const response = await api.post("/contract/getmedialink", {
      ids,
      size,
      groupId,
    });

    return response.data;
  },

  /**
   * Word-quality comparison of two document versions. The backend resolves
   * both media rows to their storage keys itself and returns a .docx whose
   * differences are tracked revisions — the browser never re-uploads
   * documents the server already has. The error body of a blob response is a
   * Blob too, so it is decoded here to surface the server's message.
   */
  compareDocuments: async (payload: {
    contractId: string;
    originalMediaId: string;
    revisedMediaId: string;
    /** The contract's organisation (a Super Admin may view another's). */
    groupId?: string;
  }): Promise<Blob> => {
    try {
      const response = await api.post("/contract/comparedocuments", payload, {
        responseType: "blob",
      });
      return response.data as Blob;
    } catch (err) {
      const data = (err as { response?: { data?: unknown } })?.response?.data;
      if (data instanceof Blob) {
        try {
          const body = JSON.parse(await data.text());
          const message = body?.message ?? body?.Message;
          if (message) throw new Error(message);
        } catch (parsed) {
          if (parsed instanceof Error && !(parsed instanceof SyntaxError)) {
            throw parsed;
          }
        }
      }
      throw err;
    }
  },

  /**
   * Contract roles this member still holds on unfinished workflows. Removing
   * or deactivating them is refused server-side while any of these exist,
   * because the contract would be left with nobody able to act on it.
   */
  getMemberContractAssignments: async (
    memberId: string,
  ): Promise<{
    memberId: string;
    assignments: {
      contractId: string;
      contractNumber: string | null;
      contractTitle: string | null;
      role: string;
      contractStatus: number;
      contractStatusLabel: string;
      isBlockingNow: boolean;
    }[];
  }> => {
    const response = await api.post("/contract/getmembercontractassignments", {
      memberId,
    });
    return {
      memberId: response.data?.memberId ?? memberId,
      assignments: response.data?.assignments ?? [],
    };
  },

  reassignContractRoles: async (payload: {
    fromMemberId: string;
    reassignments: {
      contractId: string;
      role: string;
      toMemberId: string;
      toMemberName?: string;
    }[];
  }) => {
    return await api.post("/contract/reassigncontractroles", payload);
  },

  searchContractsType: async (payload: any) => {
    const response = await api.post("/contractType/search", payload);
    return response.data;
  },

  searchContractsCateg: async (payload: any) => {
    const response = await api.post("/contractCategory/search", payload);
    return response.data;
  },

  createContractsCateg: async (data: any) => {
    const response = await api.post("/contractCategory/create", data);
    return response.data;
  },

  searchContractsTitle: async (payload: any) => {
    const response = await api.post("/contractTitle/search", payload);
    return response.data;
  },

  createContractsTitle: async (data: any) => {
    const response = await api.post("/contractTitle/create", data);
    return response.data;
  },

  searchEntityType: async (payload: any) => {
    const response = await api.post("/entityType/search", payload);
    return response.data;
  },
};
